import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormWrapper, FormRow, Label, TextArea, SubmitButton, PageContainer, ContentContainer, CancelButton } from "../styles/ComponentStyles";
import { StatusSelect, BillNumberInput, HelperText } from "../styles/BillsFormStyles";
import { BillPaymentTypesDropdown, BillTypeDropdown, BillersDropdown } from "../components/Dropdowns";
import FlashMessage from "../components/FlashMessage";
import useBills from "../hooks/useBills";
import apiService from "../services/apiService";

const EditBillPage = () => {
  const [flashMessage, setFlashMessage] = useState('');
  const { billId } = useParams();
  const { bills, loading, error } = useBills(1); // Assuming useBills hook fetches bills
  const navigate = useNavigate();
  const [renderer, reRender] = useState(1);
  const [billData, setBillData] = useState(null);

  const [formData, setFormData] = useState({
    biller_id: "",
    biller: "",
    bill_type: "",
    bill_payment_type: "",
    amount: "",
    due_date: "",
    platform: "",
    store: "",
    remarks: "",
    status: "active",
    payment_frequency: "one_time",
    due_months: "",
  });

  const handleFlashMessage = (message) => {
    setFlashMessage(message);
  };

  const handleCancel = () => {
    navigate('/bills');
  };

  // Set the bill data when fetched
  useEffect(() => {
    if (billId && bills.length > 0) {
      const selectedBill = bills.find(bill => bill._id === billId);
      setBillData(selectedBill);
    }
  }, [billId, bills]);

  // Set formData when billData is available
  useEffect(() => {
    if (billData) {
      setFormData({
        biller_id: billData.biller_id || "",
        biller: billData.biller,
        bill_type: billData.bill_type,
        bill_payment_type: billData.bill_payment_type,
        amount: billData.amount,
        due_date: billData.due_date,
        platform: billData.platform,
        store: billData.store,
        remarks: billData.remarks,
        status: billData.status || "active",
        payment_frequency: billData.payment_frequency ?? "one_time",
        due_months: billData.due_months ?? "",
      });
    }
  }, [billData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFlashMessage('');
    try {
      const payload = {
        ...formData,
        payment_frequency: formData.payment_frequency || 'one_time',
      };
      // Editing never regenerates payables — this only keeps the stored number
      // of monthly cycles in step with the form.
      const months = parseInt(formData.due_months, 10);
      payload.due_months =
        formData.payment_frequency === 'monthly' && Number.isInteger(months) && months > 0
          ? months
          : null;

      const result = await apiService.putBill(billId, payload);
      handleFlashMessage(result.message + " Redirecting. . .");
      setTimeout(() => {
        navigate("/bills");
      }, 2000);
    } catch (error) {
      // Log the error message for debugging
      if (error.response) {
        console.error("Error response:", error.response.detail);
        handleFlashMessage(error.response.data.detail);
      } else {
        console.error("Error:", error.message);
        handleFlashMessage(error.message);
      }

      reRender((prev) => prev + 1)
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading bills data</div>;

  return (
    <PageContainer>
      <ContentContainer>
        {flashMessage && <FlashMessage message={flashMessage} />}
        <FormWrapper>
          <h2 className="text-center mb-4 text-2xl">Edit Bill</h2>
          <form onSubmit={handleSubmit}>
            <FormRow>
              <Label htmlFor="biller">Biller</Label>
              <BillersDropdown
                value={formData.biller_id}
                onChange={(id, name) => {
                  setFormData(prev => ({
                    ...prev,
                    biller_id: id,
                    biller: name
                  }));
                }}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="bill_type">Bill Type</Label>
              <BillTypeDropdown
                id="bill_type"
                name="bill_type"
                value={formData.bill_type}
                onChange={handleChange}
              >
              </BillTypeDropdown>
            </FormRow>
            <FormRow>
              <Label htmlFor="bill_payment_type">Bill Payment Type</Label>
              <BillPaymentTypesDropdown
                id="bill_payment_type"
                name="bill_payment_type"
                value={formData.bill_payment_type}
                onChange={handleChange}
              >
              </BillPaymentTypesDropdown>
            </FormRow>
            <FormRow>
              <Label htmlFor="amount">Amount</Label>
              <input
                id="amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="due_date">Due Date</Label>
              <input
                id="due_date"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="platform">Platform</Label>
              <input
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="store">Store</Label>
              <input
                id="store"
                name="store"
                value={formData.store}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="status">Status</Label>
              <StatusSelect
                id="status"
                name="status"
                value={formData.status || "active"}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </StatusSelect>
            </FormRow>
            <FormRow>
              <Label htmlFor="payment_frequency">Payment frequency</Label>
              <StatusSelect
                id="payment_frequency"
                name="payment_frequency"
                value={formData.payment_frequency || "one_time"}
                onChange={handleChange}
              >
                <option value="one_time">One-time</option>
                <option value="monthly">Monthly</option>
              </StatusSelect>
            </FormRow>
            {formData.payment_frequency === "monthly" && (
              <FormRow>
                <Label htmlFor="due_months">Number of months</Label>
                <BillNumberInput
                  id="due_months"
                  name="due_months"
                  min="1"
                  max="120"
                  step="1"
                  value={formData.due_months || ""}
                  onChange={handleChange}
                />
                <HelperText>
                  Saving this does not add or remove existing due cycles — use Generate in
                  the bill's Payables view for that.
                </HelperText>
              </FormRow>
            )}
            <FormRow>
              <Label htmlFor="remarks">Remarks</Label>
              <TextArea
                name="remarks"
                value={formData.remarks || ""}
                onChange={handleChange}
              />
            </FormRow>
            <SubmitButton type="submit" onClick={handleSubmit}>Save Changes</SubmitButton>
            <CancelButton type="button" onClick={handleCancel}>Cancel</CancelButton>
          </form>
        </FormWrapper>
      </ContentContainer>
    </PageContainer>
  );
};

export default EditBillPage;
